import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ticketsKeys } from "@/lib/api/tickets";
import { knowledgeBaseKeys } from "@/lib/api/knowledge-base";
import { forumIngestionKeys } from "@/lib/api/forum-ingestion";
import type { WSServerMessage } from "@discord-bldr/shared";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

function getWSUrl(): string {
  return API_URL.replace(/^http/, "ws") + "/ws";
}

// ── Types ──

type MessageListener = (event: WSServerMessage) => void;

interface WSContextValue {
  connected: boolean;
  subscribeGuild: (applicationGuildId: string) => void;
  unsubscribeGuild: (applicationGuildId: string) => void;
  subscribeTicket: (ticketId: number) => void;
  unsubscribeTicket: (ticketId: number) => void;
  addListener: (callback: MessageListener) => () => void;
}

const WSContext = createContext<WSContextValue | null>(null);

// ── Worker ↔ Tab Protocol ──

interface WorkerConnectedMsg {
  type: "ws:connected";
}
interface WorkerDisconnectedMsg {
  type: "ws:disconnected";
  code: number;
}
interface WorkerMessageMsg {
  type: "ws:message";
  data: WSServerMessage;
}

type WorkerToTabMessage =
  | WorkerConnectedMsg
  | WorkerDisconnectedMsg
  | WorkerMessageMsg;

// ── Shared Transport Interface ──

interface WSTransport {
  send: (msg: object) => void;
  close: () => void;
  onMessage: (handler: (msg: WorkerToTabMessage) => void) => void;
}

// ── SharedWorker Transport ──

function createSharedWorkerTransport(): WSTransport {
  const worker = new SharedWorker(
    new URL("../workers/ws-shared-worker.ts", import.meta.url),
    { type: "module", name: "discord-bldr-ws" }
  );
  const port = worker.port;
  port.start();

  // Send init message with WS URL
  port.postMessage(JSON.stringify({ type: "init", wsUrl: getWSUrl() }));

  let handler: ((msg: WorkerToTabMessage) => void) | null = null;

  port.onmessage = (evt) => {
    if (!handler) return;
    try {
      const msg = JSON.parse(
        typeof evt.data === "string" ? evt.data : String(evt.data)
      ) as WorkerToTabMessage;
      handler(msg);
    } catch {
      // ignore
    }
  };

  // Send close signal on tab unload
  const onUnload = () => {
    port.postMessage(JSON.stringify({ type: "port:close" }));
  };
  window.addEventListener("beforeunload", onUnload);

  return {
    send(msg) {
      port.postMessage(JSON.stringify(msg));
    },
    close() {
      window.removeEventListener("beforeunload", onUnload);
      port.postMessage(JSON.stringify({ type: "port:close" }));
      port.close();
    },
    onMessage(cb) {
      handler = cb;
    },
  };
}

// ── Direct WebSocket Fallback Transport ──

const MAX_RECONNECT_DELAY = 30_000;
const BASE_RECONNECT_DELAY = 1_000;

function createDirectWSTransport(): WSTransport {
  let ws: WebSocket | null = null;
  let handler: ((msg: WorkerToTabMessage) => void) | null = null;
  let active = true;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function connect() {
    if (!active) return;

    const socket = new WebSocket(getWSUrl());
    ws = socket;

    socket.onopen = () => {
      if (!active) {
        socket.close();
        return;
      }
      reconnectAttempt = 0;
      handler?.({ type: "ws:connected" });
    };

    socket.onmessage = (evt) => {
      if (!active || !handler) return;
      try {
        const data = JSON.parse(evt.data as string) as WSServerMessage;
        handler({ type: "ws:message", data });
      } catch {
        // ignore
      }
    };

    socket.onclose = (evt) => {
      ws = null;
      if (!active) return;
      handler?.({ type: "ws:disconnected", code: evt.code });

      if (evt.code === 4001) return;

      const delay = Math.min(
        BASE_RECONNECT_DELAY * 2 ** reconnectAttempt,
        MAX_RECONNECT_DELAY
      );
      reconnectAttempt++;
      reconnectTimer = setTimeout(connect, delay);
    };

    socket.onerror = () => {};
  }

  connect();

  return {
    send(msg) {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      }
    },
    close() {
      active = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        ws.close();
        ws = null;
      }
    },
    onMessage(cb) {
      handler = cb;
    },
  };
}

// ── Provider ──

export function WSProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const transportRef = useRef<WSTransport | null>(null);
  const listenersRef = useRef<Set<MessageListener>>(new Set());
  const activeGuildSubs = useRef<Set<string>>(new Set());
  const activeTicketSubs = useRef<Set<number>>(new Set());
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const transport =
      typeof SharedWorker !== "undefined"
        ? createSharedWorkerTransport()
        : createDirectWSTransport();

    transportRef.current = transport;

    transport.onMessage((msg) => {
      switch (msg.type) {
        case "ws:connected":
          setConnected(true);
          // Re-subscribe all active subscriptions after reconnect
          for (const guildId of activeGuildSubs.current) {
            transportRef.current?.send({
              type: "subscribe:guild",
              applicationGuildId: guildId,
            });
          }
          for (const ticketId of activeTicketSubs.current) {
            transportRef.current?.send({
              type: "subscribe:ticket",
              ticketId,
            });
          }
          break;
        case "ws:disconnected":
          setConnected(false);
          if (msg.code === 4001) {
            void navigateRef.current({ to: "/" });
          }
          break;
        case "ws:message":
          for (const listener of listenersRef.current) {
            listener(msg.data);
          }
          break;
      }
    });

    return () => {
      transport.close();
      transportRef.current = null;
      activeGuildSubs.current.clear();
      activeTicketSubs.current.clear();
      setConnected(false);
    };
  }, []);

  const subscribeGuild = useCallback((applicationGuildId: string) => {
    activeGuildSubs.current.add(applicationGuildId);
    transportRef.current?.send({ type: "subscribe:guild", applicationGuildId });
  }, []);

  const unsubscribeGuild = useCallback((applicationGuildId: string) => {
    activeGuildSubs.current.delete(applicationGuildId);
    transportRef.current?.send({
      type: "unsubscribe:guild",
      applicationGuildId,
    });
  }, []);

  const subscribeTicket = useCallback((ticketId: number) => {
    activeTicketSubs.current.add(ticketId);
    transportRef.current?.send({ type: "subscribe:ticket", ticketId });
  }, []);

  const unsubscribeTicket = useCallback((ticketId: number) => {
    activeTicketSubs.current.delete(ticketId);
    transportRef.current?.send({ type: "unsubscribe:ticket", ticketId });
  }, []);

  const addListener = useCallback((callback: MessageListener) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  return (
    <WSContext.Provider
      value={{
        connected,
        subscribeGuild,
        unsubscribeGuild,
        subscribeTicket,
        unsubscribeTicket,
        addListener,
      }}
    >
      {children}
    </WSContext.Provider>
  );
}

// ── Hooks ──

export function useWS(): WSContextValue {
  const ctx = useContext(WSContext);
  if (!ctx) throw new Error("useWS must be used within a WSProvider");
  return ctx;
}

/**
 * Convenience hook that subscribes to a guild on mount and unsubscribes on cleanup.
 * Handles guild-level query invalidation internally.
 */
export function useGuildWS(applicationGuildId: string) {
  const {
    connected,
    subscribeGuild,
    unsubscribeGuild,
    subscribeTicket,
    unsubscribeTicket,
    addListener,
  } = useWS();
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  // Subscribe to guild
  useEffect(() => {
    subscribeGuild(applicationGuildId);
    return () => {
      unsubscribeGuild(applicationGuildId);
    };
  }, [applicationGuildId, subscribeGuild, unsubscribeGuild]);

  // Guild-level query invalidation
  useEffect(() => {
    const remove = addListener((msg) => {
      if (
        msg.type === "ticket:created" ||
        msg.type === "ticket:closed" ||
        msg.type === "ticket:status_changed"
      ) {
        queryClientRef.current.invalidateQueries({
          queryKey: ticketsKeys.stats(applicationGuildId),
        });
        queryClientRef.current.invalidateQueries({
          queryKey: ticketsKeys.list(applicationGuildId),
        });
      }

      if (
        msg.type === "scrape:progress" ||
        msg.type === "scrape:completed" ||
        msg.type === "scrape:failed"
      ) {
        queryClientRef.current.invalidateQueries({
          queryKey: knowledgeBaseKeys.all,
        });
      }

      if (
        msg.type === "forum:progress" ||
        msg.type === "forum:completed" ||
        msg.type === "forum:error"
      ) {
        queryClientRef.current.invalidateQueries({
          queryKey: forumIngestionKeys.sources(applicationGuildId),
        });
        if (msg.type === "forum:completed") {
          queryClientRef.current.invalidateQueries({
            queryKey: knowledgeBaseKeys.documents(applicationGuildId),
          });
          queryClientRef.current.invalidateQueries({
            queryKey: knowledgeBaseKeys.status(applicationGuildId),
          });
        }
      }

      if (msg.type === "document:created" || msg.type === "document:updated") {
        queryClientRef.current.invalidateQueries({
          queryKey: knowledgeBaseKeys.documents(applicationGuildId),
        });
        queryClientRef.current.invalidateQueries({
          queryKey: knowledgeBaseKeys.status(applicationGuildId),
        });
        queryClientRef.current.invalidateQueries({
          queryKey: knowledgeBaseKeys.personality(applicationGuildId),
        });
      }

      if (msg.type === "discord:updated") {
        switch (msg.resource) {
          case "channels":
            queryClientRef.current.invalidateQueries({
              queryKey: knowledgeBaseKeys.channels(applicationGuildId),
            });
            break;
          case "roles":
            queryClientRef.current.invalidateQueries({
              queryKey: knowledgeBaseKeys.roles(applicationGuildId),
            });
            break;
          case "emojis":
            queryClientRef.current.invalidateQueries({
              queryKey: knowledgeBaseKeys.emojis(applicationGuildId),
            });
            break;
          case "members":
            queryClientRef.current.invalidateQueries({
              queryKey: ticketsKeys.all,
              predicate: (query) => query.queryKey.includes("eligible-staff"),
            });
            break;
        }
      }
    });
    return remove;
  }, [applicationGuildId, addListener]);

  return { connected, subscribeTicket, unsubscribeTicket, addListener };
}

/**
 * Thin wrapper for dashboard-level WS consumers (notifications, etc.)
 */
export function useDashboardWS() {
  const { connected, addListener } = useWS();
  return { connected, addListener };
}
